import { useEffect, useState } from 'react';
import PageHeader from '../components/layout/PageHeader';
import YieldSimulatorWidget from '../components/YieldSimulatorWidget';
import portfolioService, { type ProductSummary } from '../services/portfolio.service';
import { getApiErrorMessage } from '../lib/apiError';
import { colors, gradients } from '../styles/theme';
import { formatDate, formatMoney } from '../lib/datetime';
import OfferingNotice from '../components/OfferingNotice';
import RiskDisclaimer from '../components/RiskDisclaimer';

// ─── Comparison table (standalone page only) ──────────────────────

interface ComparisonRowProps {
  readonly product: ProductSummary;
  readonly amount: number;
  readonly isLast: boolean;
}

function ComparisonRow({ product, amount, isLast }: ComparisonRowProps): JSX.Element {
  const rate = product.target_yield
    ? (Number.parseFloat(product.target_yield) * 100).toFixed(2)
    : null;
  const termLabel = product.term_days ? `${product.term_days}d` : 'Open';
  const ccy = product.currency_code;

  let yieldAmount: number | null = null;
  let totalReceived: number | null = null;
  let maturityDate: Date | null = null;

  if (rate !== null && amount > 0 && product.target_yield !== null) {
    const r = Number.parseFloat(product.target_yield);
    if (product.term_days) {
      yieldAmount = (amount * r * product.term_days) / 365;
      maturityDate = new Date();
      maturityDate.setDate(maturityDate.getDate() + product.term_days);
    } else {
      yieldAmount = amount * r;
    }
    totalReceived = amount + yieldAmount;
  }

  const yieldColor = yieldAmount === null ? colors.textMuted : colors.success;
  const yieldCell = yieldAmount === null ? '—' : formatMoney(yieldAmount, ccy);
  const totalCell = totalReceived === null ? '—' : formatMoney(totalReceived, ccy);
  const minInvestCell = formatMoney(product.min_investment, ccy);
  const maturityCell = maturityDate === null ? '—' : formatDate(maturityDate);

  return (
    <tr style={{ borderBottom: isLast ? undefined : `1px solid ${colors.border}` }}>
      <td style={{ padding: '12px 14px', fontWeight: 500, color: colors.ink }}>{product.name}</td>
      <td style={{ padding: '12px 14px', color: colors.textMuted, textAlign: 'center' }}>{termLabel}</td>
      <td style={{ padding: '12px 14px', fontWeight: 700, color: colors.navy, textAlign: 'right' }}>
        {rate === null ? '—' : `${rate}%`}
      </td>
      <td style={{ padding: '12px 14px', color: colors.textMuted, textAlign: 'right' }}>
        {minInvestCell}
      </td>
      <td style={{ padding: '12px 14px', fontWeight: 600, color: yieldColor, textAlign: 'right' }}>
        {yieldCell}
      </td>
      <td style={{ padding: '12px 14px', fontWeight: 600, color: colors.ink, textAlign: 'right' }}>
        {totalCell}
      </td>
      <td style={{ padding: '12px 14px', color: colors.textMuted, textAlign: 'right', fontSize: 12 }}>
        {maturityCell}
      </td>
    </tr>
  );
}

function thAlign(i: number): 'right' | 'center' | 'left' {
  if (i > 1) return 'right';
  if (i === 1) return 'center';
  return 'left';
}

// ─── Page ─────────────────────────────────────────────────────────

export default function Simulator(): JSX.Element {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prods = await portfolioService.listProducts();
        if (!cancelled) setProducts(prods);
      } catch (e: unknown) {
        if (!cancelled) setError(getApiErrorMessage(e, 'Could not load the series.'));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Simulators only consider products whose terms funds actually answered
  // (0073) — a null yield/term/minimum here means "unavailable", not zero.
  // Fixed-income notes only (audit #140): a fund (e.g. the RE Fund) must not
  // be projected as if it were a note — its target yield is not a simple-
  // interest coupon paid at a maturity date.
  const simulatable = products.filter(
    (p) => p.terms_available && p.asset_class === 'fixed_income',
  );
  // One amount every product accepts: the largest of their minimums.
  const defaultAmount = Math.max(
    0,
    ...simulatable.map((p) => (p.min_investment ? Number.parseFloat(p.min_investment) : 0)),
  );
  const baseCcy = simulatable[0]?.currency_code ?? 'MXN';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <PageHeader title="Simulator" />

      {error && (
        <div style={{
          background: '#fee2e2', border: '1px solid #fca5a5',
          borderRadius: 8, padding: '12px 16px',
          color: '#991b1b', marginBottom: 20, fontSize: 14,
        }}>
          {error}
        </div>
      )}

      <YieldSimulatorWidget products={products} standalone />

      {simulatable.length > 1 && defaultAmount > 0 && (
        <section style={{ marginTop: 8 }}>
          <h3 style={{
            fontSize: 12, fontWeight: 700, color: colors.textMuted,
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12,
          }}>
            All series compared · {formatMoney(defaultAmount, baseCcy)}
          </h3>
          <div style={{ border: `1px solid ${colors.border}`, borderRadius: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: gradients.navy }}>
                  {['Series', 'Term', 'Annual %', 'Min. contribution', 'Result', 'Total', 'Term ends'].map((h, i) => (
                    <th key={h} style={{
                      padding: '10px 14px',
                      fontWeight: 600, fontSize: 11,
                      color: 'rgba(255,255,255,0.65)',
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                      textAlign: thAlign(i),
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ background: colors.surface }}>
                {simulatable.map((p, idx) => (
                  <ComparisonRow
                    key={p.id}
                    product={p}
                    amount={defaultAmount}
                    isLast={idx === simulatable.length - 1}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 11, color: colors.textMuted, marginTop: 10 }}>
            Every series is shown with the same {formatMoney(defaultAmount, baseCcy)}, for its own term.
          </p>
        </section>
      )}

      {/* Audit #140: the canonical disclaimer, once, as the page's one small muted
          line at its foot — it covers the simulator above and the comparison. */}
      <RiskDisclaimer compact />
      <OfferingNotice />
    </div>
  );
}
